import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Directive, DestroyRef, effect, HostListener, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ApprovalState, Client, ExportFormat, ReportType } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { ToolbarComponent } from '@fdm/shared';
import { first, switchMap, tap, finalize, Subscription, of, EMPTY } from 'rxjs';
import { ApprovalStatusNamingPipe, ApprovalStepNamingPipe } from '../../pipes';
import { ApprovalFlowService, ButtonScriptsService, FieldHighlightService, FormValidationService, REPORT_PAGE_LIST_WITH_CONDITIONS, ReportFormPage, ReportFormStore, ReportsBulkSaveService, ResolutionAndMitigationApiService } from '../../services';
import { ApprovalFlowTabComponent, EquipmentTabComponent, FindingDescriptionTabComponent, GeneralTabComponent, MitigationResolutionTabComponent, NoteAttachmentTabComponent, RiptTabComponent, RiskEvaluationComponent, fdmStepStatusConfig2 } from './tabs';
import { LoaderService } from '../../../services/loader.service';
import { ChangeReportTypeDialogComponent, ChangeReportTypeDialogPayload, ChangeReportTypeDialogResult } from '../../../shared/dialogs';
import { ActionRequiredComponent } from './shared/action-required/action-required.component';
import { AuditHistoryComponent } from '../../../audit-history/audit-history.component';

@Component({
  selector: 'app-report-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    // Material
    MaterialModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDivider,
    // Components
    ToolbarComponent,
    RiskEvaluationComponent,
    GeneralTabComponent,
    EquipmentTabComponent,
    ApprovalFlowTabComponent,
    FindingDescriptionTabComponent,
    MitigationResolutionTabComponent,
    NoteAttachmentTabComponent,
    RiptTabComponent,
    ActionRequiredComponent,
    AuditHistoryComponent,
    // Pipes
    ApprovalStepNamingPipe,
    ApprovalStatusNamingPipe,
  ],
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.scss'],
  providers: [
    ReportsBulkSaveService,
    ResolutionAndMitigationApiService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportFormComponent {
  @ViewChild(RiptTabComponent) riptTabComponent!: RiptTabComponent;
  apiClient = inject(Client);
  private _title = inject(Title);
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _store = inject(ReportFormStore);
  private _route = inject(ActivatedRoute);
  private _approvalFlowService = inject(ApprovalFlowService);
  private _router = inject(Router);
  protected buttonScripts = inject(ButtonScriptsService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _loaderService = inject(LoaderService);
  private _toasterService = inject(ToastrService);
  private _formValidationService = inject(FormValidationService);
  private _dialog = inject(MatDialog);
  highlightedField = inject(FieldHighlightService).highlightedField;
  public reportType = this._store.reportType;
  general = toSignal(this._store.general$);
  _approvalFlow = toSignal(this._store.approvalFlow$);

  private _documentTitle = effect(() => {
    const report = this.general();
    this._title.setTitle(report?.uniqueId && report.reportTitleEn
      ? `${report.uniqueId}_${report.reportTitleEn}`
      : 'FdmApp');
  });

  readonly ReportType = ReportType;


  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const indexes = REPORT_PAGE_LIST_WITH_CONDITIONS
      .filter(item => item.show.length && this.reportType() != undefined ? item.show.includes(this.reportType()!) : true)

    if (event.ctrlKey && event.key === 'ArrowLeft' && this.tabIdx > 0 && this.general()?.id) {
      this.tabIdx--
    }

    if (event.ctrlKey && event.key === 'ArrowRight' && this.tabIdx < indexes.length - 1 && this.general()?.id) {
      this.tabIdx++
    }
  }

  mitigationResolutionTab = toSignal(this._store.mitigationResolution$);

  isProcessing = this._loaderService.processing$;
  isDisabled = computed(() => {
    const tab = this.mitigationResolutionTab();
    return this.isProcessing() || !tab?.actionItems?.length;
  })


  readonly ReportFormPage = ReportFormPage;
  public tabIdx: number = 0;

  private _step: ReportFormPage = ReportFormPage.GENERAL;

  isFormValid = toSignal(this._store.isStepValid$);
  inValidFormList: string[] = [];



  // Only for testing purposes
  public currentState: ApprovalState | null = null;

  showFormState = computed(() => {
    return this.general()?.id !== undefined;
  })

  showRiskEvaluation = computed(() => {
    return !this.reportType() || this.reportType() === ReportType.IntegrityDeficiency || this.reportType() === ReportType.ReliabilityDeficiency || this.reportType() === ReportType.TaskDeferral;
  });

  showFindingDescription = computed(() => {
    return !this.reportType() || this.reportType() === ReportType.IntegrityDeficiency || this.reportType() === ReportType.ReliabilityDeficiency || this.reportType() === ReportType.IntegrityThreat;
  });

  showRIPT = computed(() => {
    return (!this.reportType() || this.reportType() === ReportType.IntegrityDeficiency || this.reportType() === ReportType.ReliabilityDeficiency || this.reportType() === ReportType.IntegrityThreat || this.reportType() === ReportType.ReliabilityThreat || this.reportType() === ReportType.AnomalyReport);
  });

  public currentStageIndex = 0;


  ript = toSignal(this._store.ript$);
  riskHighScore = toSignal(this._store.riskHighScore$);

  readonly ApprovalState = ApprovalState;
  readonly ExportFormat = ExportFormat;
  readonly isExporting = signal(false);

  public approvalStatus = computed(() => this.general()?.currentFDMStatus)
  public approvalStep = computed(() => this.general()?.currentState);

  /** Proxied from ApprovalFlowService — drives the refresh spinner */
  readonly isRefreshing = this._approvalFlowService.isRefreshing;

  onRefreshCurrentState(): void {
    this._approvalFlowService.refreshCurrentState();
  }

  private isPlanEndorseRequired = computed(() => {
    const reportType = this.reportType();
    const general = this._store;
    const criticality = general?.criticality();
    const authLevel = general?.assignedManagementAuthorizationLevel();
    const abcPrioritization = general?.abcPrioritization();

    if (reportType === ReportType.TaskDeferral) {
      // Plan Endorse is required for auth levels B, C, D
      return (criticality === 'IC3' || criticality === 'IC1' || criticality === 'IC2' ||
        criticality === 'AC1' || criticality === 'AC2') && (authLevel === 'A' || authLevel === 'B' || authLevel === 'C' || authLevel === 'D');
    }

    if (reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency) {
      return criticality === 'IC1' || criticality === 'IC2' || criticality === 'IC3' || criticality === 'AC1' || criticality === 'AC2';
    }

    if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
      return (criticality === 'IC1' || criticality === 'IC2' || criticality === 'IC3' || criticality === 'AC1' || criticality === 'AC2') && abcPrioritization === 'A';
    }

    return false;
  });

  public approvalFlowSteps = computed(() => {
    const reportType = this.reportType();
    const approvalFlowReport = this._approvalFlow();
    const currentState = this.general()?.currentState;
    let steps: ApprovalState[] = [];

    if (reportType) {
      steps = fdmStepStatusConfig2[reportType] || [];
    } else {
      steps = [ApprovalState.Draft];
    }


    // Skip terminal states so the stepper stops at the status the report had before Out of Service / Cancelled
    const filteredAndSortedHistory = approvalFlowReport?.history
      ?.filter(item => item.currentState !== null
        && item.currentState !== ApprovalState.OutOfService
        && item.currentState !== ApprovalState.Cancelled)
      ?.sort((a, b) => new Date(a.created!).getTime() - new Date(b.created!).getTime());

    const lastStatus = filteredAndSortedHistory?.length
      ? filteredAndSortedHistory[filteredAndSortedHistory.length - 1].currentState
      : null;

    // const hasRIPTScored =
    //   (this.ript()?.riptUrl != null ||this.ript())
    //   // &&
    //   // approvalFlowReport?.history?.some(item => item.currentState === ApprovalState.RIPTScoredSkipped);
    // const hasRIPTSkipped =
    //   (this.ript()?.riptUrl == null||this.ript()==null)
    //   // &&
    //   // approvalFlowReport?.history?.some(item => item.currentState === ApprovalState.RIPTScoredSkipped);


    const hasRIPTScored = this.ript()?.riptUrl != null;
    const hasRIPTSkipped = !hasRIPTScored;

    if (currentState === ApprovalState.Cancelled) {
      const lastStatusIndex = lastStatus ? steps.findIndex(s => s === lastStatus) : -1;
      const filteredSteps = lastStatusIndex >= 0 ? steps.slice(0, lastStatusIndex + 1) : steps;
      return [...filteredSteps, ApprovalState.Cancelled];
    }
    if (currentState === ApprovalState.OutOfService) {
      const lastStatusIndex = lastStatus ? steps.findIndex(s => s === lastStatus) : -1;
      const filteredSteps = lastStatusIndex >= 0 ? steps.slice(0, lastStatusIndex + 1) : steps;
      return [...filteredSteps, ApprovalState.OutOfService];
    }

    return steps.map((step) => {
      // if (this.reportType() === ReportType.AnomalyReport) {
      //   if (step === ApprovalState.RIPTScoredSkipped) {
      //     return hasRIPTScored ? ApprovalState.RIPTScored : ApprovalState.RIPTSkipped;
      //   }
      // }
      return step;
    }).filter((step) => {
      if (step === ApprovalState.PlanEndorsed && !this.isPlanEndorseRequired()) {
        return false;
      }
      if (this.reportType() === ReportType.AnomalyReport) {
        // if (step === ApprovalState.RIPTScoredSkipped) {
        //   return hasRIPTScored || hasRIPTSkipped;
        // }
        if (step === ApprovalState.RIPTScored && hasRIPTSkipped) {
          return false;
        }
        // if (step === ApprovalState.RIPTSkipped && hasRIPTScored) {
        //   return false;
        // }
      }
      return true;
    });
  });

  public isTerminalState = computed(() => {
    const approvalStep = this.approvalStep();
    return approvalStep === ApprovalState.Cancelled || approvalStep === ApprovalState.OutOfService;
  });

  public approvalStepIndex = computed(() => {
    const approvalFlowSteps = this.approvalFlowSteps();
    const approvalStep = this.approvalStep();
    const DRAFT_IDX = 0;

    if (approvalStep === ApprovalState.Cancelled || approvalStep === ApprovalState.OutOfService) {
      return approvalFlowSteps.length - 1;
    }

    // if (this.reportType() === ReportType.AnomalyReport && approvalStep === ApprovalState.RIPTScoredSkipped) {
    //   const hasRIPTScored = this.ript()?.riptUrl != null;
    //   const mappedStep = hasRIPTScored ? ApprovalState.RIPTScored : ApprovalState.RIPTSkipped;

    //   return approvalFlowSteps.findIndex(step => step === mappedStep);
    // }

    return approvalStep != undefined ? approvalFlowSteps.findIndex(step => step === approvalStep) : DRAFT_IDX;
  });

  public riskScore = computed(() => {
    const approvalFlowReport = this._approvalFlow();
    const reportType = this.reportType();
    const general = this.general();
    const ript = this.ript();
    const riskHighScore = this.riskHighScore();
    let label = '';
    let value: string | undefined;

    if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
      label = 'RiskMan Risk Score';
      value = ript?.riskManRiskScore;
    } else if (reportType === ReportType.AnomalyReport &&
      (
        // approvalFlowReport?.history?.some(item => item.currentState === ApprovalState.RIPTScoredSkipped) ||
        // general?.currentState === ApprovalState.RIPTScoredSkipped
        ript?.riskManRiskScore
      )
    ) {
      label = 'RiskMan Risk Score';
      value = ript?.riskManRiskScore;
    } else if (reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.TaskDeferral) {
      label = 'Risk Evaluation Score';
      value = riskHighScore?.toString();
    }

    return { label, value };
  })

  constructor() {
    this._destroyRef.onDestroy(() => this._title.setTitle('FdmApp'));

    this._store.reportType$
      .pipe(
        switchMap(reportType => this._route.queryParamMap.pipe(
          tap(params => {
            const step = params.get('step');

            this._step = step != undefined ? step as ReportFormPage : ReportFormPage.GENERAL;

            this._store.changePage(this._step);

            this.tabIdx = REPORT_PAGE_LIST_WITH_CONDITIONS
              .filter(item => item.show.length && reportType != undefined ? item.show.includes(reportType) : true)
              .findIndex(item => item.page === this._step);

            this._cdr.markForCheck();
          })
        )),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  updateRIPT() {
    this._loaderService.startProcessing();

    if (!this.ript()) {
      this._loaderService.stopProcessing();
      this._toasterService.error('No RIPT url available to update.');
      return;
    }
    const riptUrl = this.ript()?.riptUrl ?? 'null';
    if (riptUrl && riptUrl !== 'null') {


      if (this.riptTabComponent) {
        this.riptTabComponent.getRiptData(riptUrl);
        // this._toasterService.success('RIPT URL updated successfully.');
      }
    } else {
      this._loaderService.stopProcessing();
      this._toasterService.error('No RIPT url available to update.');

    }
  }

  getTabClass(tab: string | undefined) {
    if (!tab) return '';

    if (this.approvalStep() !== ApprovalState.Cancelled &&
      this.approvalStep() !== ApprovalState.OutOfService &&
      this.approvalStep() !== ApprovalState.Closed) {

      if (this._formValidationService.getInvalidForms().includes(tab)) {
        return 'highlight-tab';
      }
    }

    return '';

  }


  public pageChange(idx: number) {
    this.tabIdx = idx;

    const reportType = this.reportType();

    const indexes = REPORT_PAGE_LIST_WITH_CONDITIONS
      .filter(item => item.show.length && reportType != undefined ? item.show.includes(reportType) : true)

    const step = indexes[idx].page;

    this._store.changePage(step);

    this._router.navigate([], { queryParams: { step }, queryParamsHandling: 'merge' });
  }

  public onSubmit(closeReport = false): void {
    if (this._formValidationService.areAllFormsValid()) {
      this._bulkSave.save();
    }
    else {
      this._bulkSave.save();
      // this._toasterService.error(`Some forms are invalid. Please check and correct the errors before submitting. ${this._formValidationService.getInvalidForms()}`, 'Form Validation Error');
    }

    this._store.closeReport(closeReport);
  }

  public changeApprovalFlowState(state: ApprovalState) {
    const skipSave = state === ApprovalState.Recycle || state === ApprovalState.Cancelled || state === ApprovalState.OutOfService;

    if (skipSave) {
      this._approvalFlowService.confirmStateChange(state).pipe(first()).subscribe(() => {
        this.currentState = state;
      });
      return;
    }

    this._bulkSave.saveComplete
      .pipe(
        first(),
        switchMap(() => this._approvalFlowService.confirmStateChange(state))
      )
      .subscribe(() => {
        this.currentState = state;
      });

    this.onSubmit();
  }

  public reactivateReport() {
    this._approvalFlowService.confirmReactivate().pipe(first()).subscribe();
  }

  private getListPageRoute(): string {
    const currentUrl = this._router.url;
    if (currentUrl.includes('/my-approvals')) {
      return '/my-approvals';
    }
    return '/reports';
  }

  public createNewReport(): void {
    const listRoute = this.getListPageRoute();
    this._router.navigate([`${listRoute}/add`]);
    this._store.restore();
    // this._approvalFlow.reset();
  }
  public changeReportType(): void {
    const currentReportType = this.reportType();

    if (!currentReportType) {
      this._toasterService.error('No report type selected');
      return;
    }
    let filteredOptions: Array<{ value: ReportType }> = [];
    // ITR (Integrity Threat ) or RT (Reliability Threat)
    // Can change to: RD (Reliability Deficiency), ID (Integrity Deficiency), AR (Anomaly Report)
    if (currentReportType === ReportType.IntegrityThreat || currentReportType === ReportType.ReliabilityThreat) {
      filteredOptions = [
        { value: ReportType.ReliabilityDeficiency },
        { value: ReportType.IntegrityDeficiency },
        { value: ReportType.AnomalyReport }
      ];
    }
    // RD (Reliability Deficiency), ID (Integrity Deficiency), or AR (Anomaly Report)
    // Can change to: ITR (Integrity Threat = Task Deferral), RT (Reliability Threat = Threat Deficiency)
    else if (
      currentReportType === ReportType.ReliabilityDeficiency ||
      currentReportType === ReportType.IntegrityDeficiency ||
      currentReportType === ReportType.AnomalyReport
    ) {
      filteredOptions = [
        { value: ReportType.IntegrityThreat },
        { value: ReportType.ReliabilityThreat }
      ];
    }

    if (filteredOptions.length === 0) {
      this._toasterService.error('No valid report type options available for change');
      return;
    }

    const dialogData: ChangeReportTypeDialogPayload = {
      options: filteredOptions,
      subheading: 'Are you sure you want to change the report type?',
      reportTypeLabel: 'New Report Type:',
      justificationLabel: 'Justification:',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      footerNote: 'Justification is required'
    };

    const dialogRef = this._dialog.open(ChangeReportTypeDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true,
      panelClass: 'change-report-type-dialog'
    });

    dialogRef.afterClosed().subscribe((result: ChangeReportTypeDialogResult | undefined) => {
      if (result && result.status === 'Success') {
        const reportId = this.general()?.id;

        if (!reportId) {
          this._toasterService.error('Report ID not found');
          return;
        }

        this._loaderService.startProcessing();

        this.apiClient.reportType(reportId, result.reportType /*,result.justification*/)
          .pipe(
            first(),
            finalize(() => this._loaderService.stopProcessing())
          )
          .subscribe({
            next: () => {
              this._toasterService.success('Report type changed successfully');
              window.location.reload();
            },
            error: (error) => {
              this._toasterService.error('Failed to change report type: ' + (error.message || 'Unknown error'));
            }
          });
      }
    });
  }

  requestRIPT() {
    const reportId = this.general()?.id;

    if (!reportId) {
      return;
    }

    this._loaderService.startProcessing();
    this.apiClient.requestRipt(reportId)
      .pipe(
        switchMap(() => this.apiClient.byGeneralTab(reportId)),
        finalize(() => this._loaderService.stopProcessing())
      )
      .subscribe((data) => {
        this._store.setApprovalFlow(data);
        this._toasterService.success('RIPT requested successfully');
      });
  }

  exportReport(format: ExportFormat): void {
    const reportId = this.general()?.id;

    if (!reportId) {
      this._toasterService.error('Save the report first to export it.');
      return;
    }

    this.isExporting.set(true);

    this.apiClient.export(reportId, format)
      .pipe(finalize(() => this.isExporting.set(false)))
      .subscribe({
        next: (response) => {
          const file = response.data as Blob;

          if (!file || file.size === 0) {
            this._toasterService.error('Export failed: empty file received.');
            return;
          }

          const fileName = response.fileName ?? this._buildDefaultExportFileName(format);
          this._downloadFile(file, fileName);
          this._toasterService.success(`Exported to ${format}.`);
        },
        error: (error) => {
          if (error?.status === 404) {
            this._toasterService.error('Report not found for export.');
            return;
          }

          this._toasterService.error('Failed to export report. Please try again.');
        }
      });
  }

  private _buildDefaultExportFileName(format: ExportFormat): string {
    const report = this.general();
    const fileBaseName = report?.uniqueId ?? report?.reportTitleEn ?? 'fdm-report';
    const extensionByFormat: Record<ExportFormat, string> = {
      [ExportFormat.Excel]: 'xlsx',
      [ExportFormat.Word]: 'docx',
      [ExportFormat.Pdf]: 'pdf'
    };

    return `${fileBaseName}.${extensionByFormat[format]}`;
  }

  private _downloadFile(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(objectUrl);
  }
}
